// const moduleB = () => {
//   console.log("component other");
// };
import { ComponentsManager, Component, EventBus } from '../core/smpl-components.js'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { AnchorScroller } from '../modules/anchor-scroller'
import { ToggleButton } from '../modules/toggle-button.js'

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(ScrollToPlugin)

class StickyHeaderBar {
	constructor(options = {}) {
		this.options = {
			element: document.querySelector('header'),
			offset: 100,
			tolerance: 2,
			showAtBottom: true,
			animate: true, // animate with GSAP
			duration: 0.3,
			ease: 'power2.out',
			classNames: {
				base: 'sticky-header',
				fixed: 'sticky-header--fixed',
				hidden: 'sticky-header--hidden',
				notTop: 'sticky-header--not-top',
				pastOffset: 'sticky-header--past-offset',
			},
			...options,
		}

		this.header = this.options.element
		if (!Array.isArray(this.header)) {
			this.header = [this.header]
		}
		this.lastScroll = window.scrollY

		if (this.header) {
			this.init()
		}
	}

	init() {
		gsap.registerPlugin(ScrollTrigger)

		this.addClass(this.options.classNames.base)

		this.scrollTrigger = ScrollTrigger.create({
			start: 0,
			end: 'max',
			onUpdate: () => {
				if (!this.hasInitialized) return
				if (ScrollTrigger.isScrolling()) {
					this.handleScroll()
				}
			},
		})

		// Set initial header state
		if (this.options.animate) {
			gsap.set(this.header, { yPercent: 0 })
		}

		// Update state on page load
		this.updateState()
		this.fix()

		setTimeout(() => {
			this.hasInitialized = true
		}, 100)
	}

	/**
	 * Updates the header's state (e.g., notTop, pastOffset).
	 * This method is reusable and can be called during initialization or dynamically.
	 */
	updateState() {
		const scrollCurrent = window.pageYOffset

		// Update not-top state
		if (scrollCurrent > 0) {
			this.addClass(this.options.classNames.notTop)
		} else {
			this.removeClass(this.options.classNames.notTop)
		}

		// Update past-offset state
		if (scrollCurrent > this.options.offset) {
			this.addClass(this.options.classNames.pastOffset)
		} else {
			this.removeClass(this.options.classNames.pastOffset)
		}
	}

	/**
	 * Handles scroll-specific behavior (e.g., hiding or fixing the header).
	 * This method is called on scroll events.
	 */
	handleScroll() {
		const scrollCurrent = window.pageYOffset
		const scrollLast = this.lastScroll

		const goingDown = scrollCurrent > scrollLast
		const goingUp = scrollCurrent < scrollLast
		const toleranceReached = Math.abs(scrollCurrent - scrollLast) >= this.options.tolerance

		// Update state (e.g., notTop, pastOffset)
		this.updateState()

		// Handle visibility
		if (scrollCurrent <= 0) {
			this.reset()
		} else if (this.options.showAtBottom && window.innerHeight + scrollCurrent >= document.body.offsetHeight) {
			this.fix()
		} else if (scrollCurrent > this.options.offset && toleranceReached) {
			// Scrolled past offset
			if (goingDown) {
				this.hide()
			} else if (goingUp) {
				this.fix()
			}
		}

		this.lastScroll = scrollCurrent
	}

	reset() {
		this.isVisible = false
		document.body.classList.remove('has-fixed-nav')

		const cn = this.options.classNames
		this.removeClass(cn.fixed, cn.hidden)
		if (this.options.animate) {
			gsap.to(this.header, {
				yPercent: 0,
				duration: this.options.duration,
				ease: this.options.ease,
			})
		}
	}

	fix() {
		this.isVisible = true
		document.body.classList.add('has-fixed-nav')

		const cn = this.options.classNames
		this.addClass(cn.fixed)
		this.removeClass(cn.hidden)
		if (this.options.animate) {
			gsap.to(this.header, {
				yPercent: 0,
				duration: this.options.duration,
				ease: this.options.ease,
			})
		}
	}

	hide() {
		this.isVisible = false
		document.body.classList.remove('has-fixed-nav')

		const cn = this.options.classNames
		this.addClass(cn.hidden)
		if (this.options.animate) {
			gsap.to(this.header, {
				yPercent: -100,
				duration: this.options.duration,
				ease: this.options.ease,
			})
		}
	}

	addClass(...args) {
		this.header.forEach((el) => {
			el.classList.add(...args)
		})
	}

	removeClass(...args) {
		this.header.forEach((el) => {
			el.classList.remove(...args)
		})
	}

	destroy() {
		if (this.scrollTrigger) {
			this.scrollTrigger.kill(true)
		}
		this.isVisible = false
		document.body.classList.remove('has-fixed-nav')

		this.removeClass(this.options.classNames.base)
		this.removeClass(this.options.classNames.fixed)
		this.removeClass(this.options.classNames.hidden)
		this.removeClass(this.options.classNames.notTop)
		this.removeClass(this.options.classNames.pastOffset)
	}
}
// class MainNavOverlay {
// 	constructor(options) {
// 		this.element = options.element
// 		this.options = {
// 			...options,
// 		}
// 	}

// 	toggle() {}
// 	init() {}
// }

class MainNav {
	constructor(element) {
		this.element = element

		this.elNavBar = this.element.querySelector('.js-nav-bar')
		this.elOverlayToggle = this.element.querySelector('.js-main-nav-toggle')
		this.elOverlay = this.element.querySelector('.main-nav__overlay')
		this.elOverlayLinks = this.elOverlay ? this.elOverlay.querySelectorAll('a[href]') : []

		this.bndToggleOverlay = this.toggleOverlay.bind(this)
		// this.bndOverlayLinkClick = this.onOverlayLinkClick.bind(this)

		if (this.elOverlayToggle) {
			this.elOverlayToggle.addEventListener('click', this.bndToggleOverlay)

			this.toggleButton = new ToggleButton(this.elOverlayToggle)
		}

		this.elOverlayLinks.forEach((el) => {
			el.addEventListener('click', this.bndOverlayLinkClick)
		})
		// this.overlay = new MainNavOverlay({element: this.elOverlay})

		this.stickyHeaderBar = new StickyHeaderBar({
			element: [this.elNavBar],
			ease: 'Power4.easeOut',
			duration: 0.5,
		})

		// this.anchorScroller = new AnchorScroller({
		// 	scope: this.elOverlay,
		// 	onBeforeScroll: () => {
		// 		if (!this.overlayVisible) return
		// 		this.hideOverlay()
		// 	},
		// })
		// this.anchorScroller.bind()

		this.overlayVisible = false

		this.bndShowOverlay = this.showOverlay.bind(this)
		this.bndHideOverlay = this.hideOverlay.bind(this)
		document.addEventListener('swup:link:self', this.bndHideOverlay)
	}

	init() {}

	toggleOverlay() {
		if (this.overlayVisible) {
			this.hideOverlay()
		} else {
			this.showOverlay()
		}
	}
	showOverlay() {
		if (this.overlayVisible) return
		if (this.timeline) this.timeline.kill()

		this.timeline = gsap.timeline({
			onComplete: () => {
				window.app.browser.disableScroll(this.elOverlay)
			},
		})

		if (this.toggleButton) this.toggleButton.on()

		this.timeline.fromTo(this.elOverlay, { display: 'block', opacity: 0 }, { opacity: 1, duration: 0.4 })

		this.elOverlay.classList.add('is-visible')
		this.element.classList.add('is-overlay-visible')
		document.body.classList.add('has-nav-overlay')
		this.overlayVisible = true
	}

	onOverlayLinkClick(event) {
		const link = event.currentTarget
		const href = link.getAttribute('href') || ''
		// Only intercept when overlay is visible
		if (!this.overlayVisible) return

		event.preventDefault()

		this.hideOverlay()

		const isHash = href.startsWith('#')
		const hash = isHash ? href : ''

		if (isHash) {
			console.log(this.anchorPositionCache)
			const target = document.querySelector(`a[name=${hash.slice(1)}]`)
			console.log('Scrolling to target:', target)
			console.log('Target Y:', this.anchorPositionCache[hash.slice(1)])
			if (target) {
				let scrollTriggerExists = getParentScrollTrigger(target)

				if (scrollTriggerExists) {
					// get the height if the pin element
					const sectionHeight = scrollTriggerExists.pin.offsetHeight
					const sectionStart = scrollTriggerExists.start - (sectionHeight - window.innerHeight)
					const anchorOffset = getOffsetToParent(target, scrollTriggerExists.pin)
					gsap.to(window, {
						scrollTo: { y: sectionStart + anchorOffset, autoKill: false },
						duration: 1,
						ease: 'power2.inOut',
					})
				}
			}
		}
	}

	hideOverlay() {
		if (!this.overlayVisible) return
		// if (this.timeline && this.timeline.isActive()) return

		if (this.timeline) this.timeline.kill()

		window.app.browser.enableScroll()

		this.timeline = gsap.timeline({
			onComplete: () => {},
		})

		this.timeline.to(this.elOverlay, {
			opacity: 0,
			onComplete: () => {
				this.elOverlay.style.display = 'none'
			},
		})

		if (this.toggleButton) this.toggleButton.off()

		this.elOverlay.classList.remove('is-visible')
		this.element.classList.remove('is-overlay-visible')
		document.body.classList.remove('has-nav-overlay')

		this.overlayVisible = false
	}
	destroy() {
		window.app.browser.enableScroll()

		if (this.toggleButton) {
			this.toggleButton.destroy()
		}
		if (this.elOverlayToggle) {
			this.elOverlayToggle.removeEventListener('click', this.bndToggleOverlay)
		}

		document.removeEventListener('swup:link:self', this.bndHideOverlay)

		document.body.classList.remove('has-nav-overlay')

		if (this.timeline) this.timeline.kill()

		if (this.stickyHeaderBar) {
			this.stickyHeaderBar.destroy()
		}
	}
}

// -------------------------------------------------------------------------- //
export default class extends Component {
	prepare() {}
	mount() {
		this.mainNav = new MainNav(this.element)

		if (matchMedia('(pointer:fine)').matches) {
			this.elOverlayNav = this.element.querySelector('.nav-overlay .menu-list')
			// enable animations
		}

		// this.headerColor = new HeaderColor(this.element, {})
	}
	unmount() {
		this.mainNav.destroy()
	}
}
